import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { ColumnDefinition, MasterTableComponent, TableAction, TableFilterDefinition } from '@shared/components/master-table/master-table.component';
import { firstValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { CompanyDirectoryService } from '@shared/services/company-directory.service';
import { CompaniesRecord } from './companies-record.interface';
import { PageEvent } from '@angular/material/paginator';

interface CompaniesRecordsResponse {
  items?: CompaniesRecord[];
  data?: CompaniesRecord[];
  results?: CompaniesRecord[];
  companiesRecords?: CompaniesRecord[];
  pagination?: {
    total?: number;
    totalItems?: number;
    count?: number;
    page?: number;
    currentPage?: number;
    perPage?: number;
    pageSize?: number;
  };
  total?: number;
  totalItems?: number;
  count?: number;
  page?: number;
  perPage?: number;
}

@Component({
  selector: 'app-companies-records',
  imports: [BreadcrumbComponent, MasterTableComponent, TranslateModule],
  templateUrl: './companies-records.component.html',
  styleUrl: './companies-records.component.scss',
})
export class CompaniesRecordsComponent implements OnInit {
  dataSource = new MatTableDataSource<CompaniesRecord>([]);
  allRecords: CompaniesRecord[] = [];
  companyFilterRecords: CompaniesRecord[] = [];
  isLoading = true;
  actions: TableAction<CompaniesRecord>[] = [];
  columnDefinitions: ColumnDefinition[] = [];
  tableFilters: TableFilterDefinition<CompaniesRecord>[] = [];
  searchValue = '';
  totalItems = 0;
  private loadRequestSequence = 0;
  page = 1;
  perPage = 15;
  activeFilters: Record<string, string | number | boolean | null> = {};
  readonly pageSizeOptions = [15, 25, 50, 100, 200];

  private apiService = inject(AuthorizedApiService);
  private companyDirectory = inject(CompanyDirectoryService);
  private authorizationService = inject(AuthorizationService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.configureTranslations();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.configureTranslations();
        this.dataSource.data = this.dataSource.data.map((company) => this.normalizeCompanyRecord(company));
      });
    void this.loadData(true);
  }

  async loadData(forceCompanyDirectory = false): Promise<void> {
    const requestId = ++this.loadRequestSequence;
    this.isLoading = true;
    try {
      const [records, directoryRecords] = await Promise.all([
        firstValueFrom(
          this.apiService.get<CompaniesRecord[] | CompaniesRecordsResponse>('companies-records', {
            params: {
              ...(this.getSelectedCompanySearch() || this.searchValue
                ? { search: this.getSelectedCompanySearch() || this.searchValue }
                : {}),
              ...(this.activeFilters['status']
                ? { isActive: this.activeFilters['status'] === 'active' ? 'true' : 'false' }
                : {}),
              page: this.page,
              perPage: this.perPage,
            },
          })
        ),
        this.companyDirectory.getAll(false, forceCompanyDirectory).catch(() => []),
      ]);
      if (requestId !== this.loadRequestSequence) return;
      if (directoryRecords.length) {
        this.companyFilterRecords = directoryRecords;
      }
      const normalizedResponse = this.normalizeResponse(records);
      this.page = normalizedResponse.page;
      this.perPage = normalizedResponse.perPage;
      this.allRecords = normalizedResponse.items.map((record) => this.normalizeCompanyRecord(record));
      this.buildTableFilters();
      this.applyFilters();
      this.totalItems = normalizedResponse.total;
    } catch (error) {
      if (requestId !== this.loadRequestSequence) return;
      console.error('Error loading company records', error);
      this.allRecords = [];
      this.dataSource.data = [];
      this.tableFilters = [];
      this.totalItems = 0;
    } finally {
      if (requestId === this.loadRequestSequence) this.isLoading = false;
    }
  }

  handleAdd(): void {
    if (!this.hasAccess('create')) {
      return;
    }
    void this.router.navigate(['/organization/companies-records/new']);
  }

  async handleEdit(row: CompaniesRecord): Promise<void> {
    if (!this.hasAccess('update')) {
      return;
    }
    await this.router.navigate(['/organization/companies-records', row.companiesRecordId, 'edit']);
  }

  async handleView(row: CompaniesRecord): Promise<void> {
    if (!this.hasAccess('read')) {
      return;
    }
    await this.router.navigate(['/organization/companies-records', row.companiesRecordId]);
  }

  handleDelete(row: CompaniesRecord): void {
    if (!this.hasAccess('delete')) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('companiesRecordsCatalog.dialog.deleteTitle'),
        message: this.t('companiesRecordsCatalog.dialog.deleteMessage', { company: row.name || row.code }),
        confirmLabel: this.t('companiesRecordsCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await firstValueFrom(
          this.apiService.delete(`companies-records/${row.companiesRecordId}`)
        );
        await this.loadData(true);
        this.showNotification('snackbar-success', this.t('companiesRecordsCatalog.notifications.deleted'), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting company record', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('companiesRecordsCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  handleBulkDelete(rows: CompaniesRecord[]): void {
    if (!this.hasAccess('delete') || !rows.length) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('common.bulkDeleteTitle'),
        message: this.t('common.bulkDeleteMessage', { count: rows.length }),
        confirmLabel: this.t('common.delete'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await Promise.all(
          rows.map((row) => firstValueFrom(this.apiService.delete(`companies-records/${row.companiesRecordId}`)))
        );
        await this.loadData(true);
        this.showNotification('snackbar-success', this.t('common.bulkDeleteSuccess', { count: rows.length }), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting company records', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('companiesRecordsCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  handleAction(event: { action: TableAction<CompaniesRecord>; selectedRows: CompaniesRecord[] }): void {
    if (event.action.behavior) {
      return;
    }
    if (event.action.id === 'details' && event.selectedRows.length === 1) {
      void this.handleView(event.selectedRows[0]);
    }
  }

  onSearchChange(search: string): void {
    this.searchValue = search;
    this.page = 1;
    void this.loadData();
  }

  onFiltersChange(filters: Record<string, string | number | boolean | null>): void {
    this.activeFilters = filters;
    this.page = 1;
    void this.loadData();
  }

  private getSelectedCompanySearch(): string {
    const companyId = String(this.activeFilters['company'] || '');
    if (!companyId) return '';
    const filter = this.tableFilters.find((item) => item.id === 'company');
    return filter?.options?.find((option) => String(option.value) === companyId)?.label || '';
  }

  onPageChange(event: PageEvent): void {
    this.page = event.pageIndex + 1;
    this.perPage = event.pageSize;
    void this.loadData();
  }

  private normalizeCompanyRecord(record: Partial<CompaniesRecord>): CompaniesRecord {
    const isActive = record.isActive !== false;

    return {
      companiesRecordId: record.companiesRecordId || crypto.randomUUID(),
      center: record.center || '',
      code: record.code || '',
      name: record.name || '',
      description: record.description || '',
      isActive,
      activeLabel: isActive ? this.t('common.status.active') : this.t('common.status.inactive'),
    };
  }

  private normalizeResponse(
    response: CompaniesRecord[] | CompaniesRecordsResponse
  ): { items: CompaniesRecord[]; total: number; page: number; perPage: number } {
    if (Array.isArray(response)) {
      return {
        items: response,
        total: response.length,
        page: this.page,
        perPage: this.perPage,
      };
    }

    const items =
      response.items ||
      response.data ||
      response.results ||
      response.companiesRecords ||
      [];

    return {
      items,
      total:
        response.pagination?.total ??
        response.pagination?.totalItems ??
        response.pagination?.count ??
        response.total ??
        response.totalItems ??
        response.count ??
        items.length,
      page:
        response.pagination?.page ??
        response.pagination?.currentPage ??
        response.page ??
        this.page,
      perPage:
        response.pagination?.perPage ??
        response.pagination?.pageSize ??
        response.perPage ??
        this.perPage,
    };
  }

  private configureTranslations(): void {
    this.actions = [
      {
        id: 'add',
        icon: 'add',
        tooltip: this.t('companiesRecordsCatalog.actions.create'),
        colorClass: 'success',
        auth: { resource: 'companiesRecords', action: 'create' },
        behavior: 'add',
      },
      {
        id: 'refresh',
        icon: 'refresh',
        tooltip: this.t('companiesRecordsCatalog.actions.refresh'),
        colorClass: 'neutral',
        auth: { resource: 'companiesRecords', action: 'read' },
        behavior: 'refresh',
      },
      {
        id: 'details',
        icon: 'visibility',
        tooltip: this.t('companiesRecordsCatalog.actions.view'),
        colorClass: 'info',
        auth: { resource: 'companiesRecords', action: 'read' },
        selectionMode: 'single',
      },
      {
        id: 'edit',
        icon: 'edit',
        tooltip: this.t('companiesRecordsCatalog.actions.edit'),
        colorClass: 'accent',
        auth: { resource: 'companiesRecords', action: 'update' },
        behavior: 'edit',
        selectionMode: 'single',
      },
      {
        id: 'delete',
        icon: 'delete_outline',
        tooltip: this.t('companiesRecordsCatalog.actions.delete'),
        colorClass: 'danger',
        auth: { resource: 'companiesRecords', action: 'delete' },
        behavior: 'delete',
        selectionMode: 'single',
      },
      {
        id: 'export',
        icon: 'file_download',
        tooltip: this.t('companiesRecordsCatalog.actions.export'),
        colorClass: 'neutral',
        auth: { resource: 'companiesRecords', action: 'read' },
        behavior: 'export',
      },
    ];

    this.columnDefinitions = [
      { def: 'select', label: this.t('common.select'), type: 'check', visible: true },
      { def: 'code', label: this.t('companiesRecordsCatalog.fields.code'), type: 'text', visible: true },
      { def: 'name', label: this.t('companiesRecordsCatalog.fields.name'), type: 'text', visible: true },
      {
        def: 'activeLabel',
        label: this.t('companiesRecordsCatalog.fields.isActive'),
        type: 'status',
        visible: true,
        statusBadgeMap: {
          [this.t('common.status.active')]: 'badge badge-solid-green',
          [this.t('common.status.inactive')]: 'badge badge-solid-red',
        },
      },
    ];

    this.buildTableFilters();
  }

  private buildTableFilters(): void {
    const companyOptions = (this.companyFilterRecords.length ? this.companyFilterRecords : this.allRecords).map((record) => ({
      value: record.companiesRecordId,
      label: record.name || record.code,
      searchText: `${record.code || ''} ${record.description || ''}`.trim(),
    }));

    const uniqueCompanyOptions = Array.from(
      new Map(companyOptions.map((option) => [option.value, option])).values()
    ).sort((left, right) => left.label.localeCompare(right.label));

    this.tableFilters = [
      {
        id: 'company',
        label: this.t('companiesRecordsCatalog.fields.name'),
        placeholder: this.t('companiesRecordsCatalog.filters.companyPlaceholder'),
        icon: 'business',
        searchable: true,
        value: this.activeFilters['company'] ?? null,
        options: uniqueCompanyOptions,
      },
      {
        id: 'status',
        label: this.t('companiesRecordsCatalog.fields.isActive'),
        placeholder: this.t('companiesRecordsCatalog.filters.statusPlaceholder'),
        icon: 'verified',
        searchable: false,
        value: this.activeFilters['status'] ?? null,
        options: [
          { value: 'active', label: this.t('common.status.active') },
          { value: 'inactive', label: this.t('common.status.inactive') },
        ],
      },
    ];
  }

  private applyFilters(): void {
    const filtered = this.allRecords.filter((record) => {
      const matchesCompany =
        !this.activeFilters['company'] ||
        record.companiesRecordId === this.activeFilters['company'];

      const matchesStatus =
        !this.activeFilters['status'] ||
        (this.activeFilters['status'] === 'active' && record.isActive) ||
        (this.activeFilters['status'] === 'inactive' && !record.isActive);

      return matchesCompany && matchesStatus;
    });

    this.dataSource.data = filtered;
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2000,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  private hasAccess(action: 'create' | 'read' | 'update' | 'delete' | 'access'): boolean {
    return this.authorizationService.hasAccess({ resource: 'companiesRecords', action });
  }

}
